<template>
  <div class="tagged-search-container">
    <!-- Search Input Area -->
    <div class="field">
      <div
        v-if="!hideHeader"
        class="is-flex is-align-items-center is-justify-content-space-between mb-3"
      >
        <label v-if="!hideLabel" class="label mb-0">{{ labelText }}</label>
        <!-- Search Mode Toggle -->
        <div v-if="!hideModeToggle" class="search-mode-toggle-inline">
          <label class="radio">
            <input
              type="radio"
              value="all"
              v-model="searchMode"
              @change="onModeChange"
            />
            <span class="radio-text">All terms</span>
          </label>
          <label class="radio">
            <input
              type="radio"
              value="any"
              v-model="searchMode"
              @change="onModeChange"
            />
            <span class="radio-text">Any terms</span>
          </label>
        </div>
      </div>
      <div
        class="control has-icons-left dropdown-container"
        :class="{ 'is-loading': isLoading }"
      >
        <div class="tagged-input-wrapper" ref="inputWrapper">
          <!-- Selected Tags -->
          <div
            v-if="selectedTags.length > 0"
            class="field is-grouped is-grouped-multiline mb-0"
          >
            <div
              v-for="(tag, index) in selectedTags"
              :key="index"
              class="control"
            >
              <div class="tags has-addons">
                <span class="tag is-primary">{{ tag }}</span>
                <a
                  class="tag is-dark"
                  @click="removeTag(index)"
                  :title="`Remove '${tag}'`"
                >
                  <font-awesome-icon :icon="['fas', 'times']" />
                </a>
              </div>
            </div>
          </div>

          <!-- Input Field -->
          <input
            ref="searchInput"
            type="text"
            class="input tagged-input"
            :class="{ 'has-tags': selectedTags.length > 0 }"
            :placeholder="selectedTags.length > 0 ? '' : placeholder"
            v-model="inputValue"
            @input="onInput"
            @focus="showDropdown = true"
            @blur="onBlur"
            @keydown="onKeyDown"
          />
        </div>

        <span class="icon is-small is-left">
          <font-awesome-icon :icon="iconName" />
        </span>

        <!-- Autocomplete Dropdown -->
        <div
          v-if="showDropdown && filteredSuggestions.length > 0"
          class="dropdown-content autocomplete-dropdown"
          ref="dropdown"
        >
          <div
            v-for="(suggestion, index) in filteredSuggestions"
            :key="index"
            class="dropdown-item"
            :class="{
              'is-hovered': index === highlightedIndex,
              'is-disabled': selectedTags.includes(suggestion.value),
            }"
            @mousedown.prevent="selectSuggestion(suggestion)"
            @mouseenter="highlightedIndex = index"
          >
            <div class="suggestion-content">
              <span class="suggestion-icon">
                <font-awesome-icon
                  :icon="[
                    'fas',
                    suggestion.type === 'tag'
                      ? 'tag'
                      : getAssetTypeIcon(suggestion.assetType),
                  ]"
                  class="has-text-primary"
                />
              </span>
              <span class="suggestion-text">{{ suggestion.value }}</span>
              <span class="suggestion-type has-text-grey is-size-7">
                {{
                  suggestion.type === "tag"
                    ? "Tag"
                    : getAssetTypeDisplayName(suggestion.assetType)
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from "vue";
import { logger } from "@/services/loggerService";
import { getAssetTypeIcon, getAssetTypeDisplayName } from "@/utils/assetUtils";

// Props
const props = defineProps({
  suggestions: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: "Search tags, keywords, asset names...",
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
  mode: {
    type: String,
    default: "any",
    validator: (value) => ["all", "any"].includes(value),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  // UI customization props
  hideHeader: {
    type: Boolean,
    default: false,
  },
  hideLabel: {
    type: Boolean,
    default: false,
  },
  hideModeToggle: {
    type: Boolean,
    default: false,
  },
  labelText: {
    type: String,
    default: "Search",
  },
  icon: {
    type: [String, Array],
    default: () => ["fal", "search"],
  },
});

// Emits
const emit = defineEmits(["update:modelValue", "update:mode", "search"]);

// Reactive state
const inputValue = ref("");
const selectedTags = ref([...props.modelValue]);
const searchMode = ref(props.mode);
const showDropdown = ref(false);
const highlightedIndex = ref(-1);
const searchInput = ref(null);
const inputWrapper = ref(null);
const dropdown = ref(null);

// Computed
const iconName = computed(() => {
  return props.icon;
});

const filteredSuggestions = computed(() => {
  if (!inputValue.value.trim()) return [];

  const query = inputValue.value.toLowerCase().trim();
  const filtered = props.suggestions
    .filter(
      (suggestion) =>
        suggestion.value.toLowerCase().includes(query) &&
        !selectedTags.value.includes(suggestion.value)
    )
    .slice(0, 10); // Limit to 10 suggestions

  return filtered;
});

// Methods
const onInput = () => {
  showDropdown.value = true;
  highlightedIndex.value = -1;
};

const onBlur = () => {
  // Delay hiding dropdown to allow for click events
  setTimeout(() => {
    showDropdown.value = false;
    highlightedIndex.value = -1;
  }, 150);
};

const onKeyDown = (event) => {
  if (!showDropdown.value) return;

  const suggestions = filteredSuggestions.value;

  switch (event.key) {
    case "ArrowDown":
      event.preventDefault();
      highlightedIndex.value = Math.min(
        highlightedIndex.value + 1,
        suggestions.length - 1
      );
      break;

    case "ArrowUp":
      event.preventDefault();
      highlightedIndex.value = Math.max(highlightedIndex.value - 1, -1);
      break;

    case "Enter":
      event.preventDefault();
      if (highlightedIndex.value >= 0 && suggestions[highlightedIndex.value]) {
        selectSuggestion(suggestions[highlightedIndex.value]);
      } else if (inputValue.value.trim()) {
        // Add as custom tag
        addCustomTag(inputValue.value.trim());
      }
      break;

    case "Escape":
      showDropdown.value = false;
      highlightedIndex.value = -1;
      searchInput.value?.blur();
      break;

    case "Backspace":
      if (!inputValue.value && selectedTags.value.length > 0) {
        removeTag(selectedTags.value.length - 1);
      }
      break;
  }
};

const selectSuggestion = (suggestion) => {
  if (selectedTags.value.includes(suggestion.value)) return;

  selectedTags.value.push(suggestion.value);
  inputValue.value = "";
  showDropdown.value = false;
  highlightedIndex.value = -1;

  // Focus back to input
  nextTick(() => {
    searchInput.value?.focus();
  });

  emitUpdate();
  logger.debug("[TaggedSearchInput] Added suggestion:", {
    tag: suggestion.value,
    type: suggestion.type,
  });
};

const addCustomTag = (value) => {
  if (selectedTags.value.includes(value)) return;

  selectedTags.value.push(value);
  inputValue.value = "";
  showDropdown.value = false;

  emitUpdate();
  logger.debug("[TaggedSearchInput] Added custom tag:", { tag: value });
};

const removeTag = (index) => {
  const removedTag = selectedTags.value[index];
  selectedTags.value.splice(index, 1);
  emitUpdate();
  logger.debug("[TaggedSearchInput] Removed tag:", { tag: removedTag });
};

const onModeChange = () => {
  emit("update:mode", searchMode.value);
  emitUpdate();
  logger.debug("[TaggedSearchInput] Changed search mode:", {
    mode: searchMode.value,
  });
};

const emitUpdate = () => {
  emit("update:modelValue", [...selectedTags.value]);
  emit("search", {
    tags: [...selectedTags.value],
    mode: searchMode.value,
  });
};

// Watch for external changes
watch(
  () => props.modelValue,
  (newValue) => {
    selectedTags.value = [...newValue];
  },
  { deep: true }
);

watch(
  () => props.mode,
  (newMode) => {
    searchMode.value = newMode;
  }
);

// Lifecycle
onMounted(() => {
  logger.debug("[TaggedSearchInput] Component mounted");
});
</script>

<style lang="scss" scoped>
.tagged-search-container {
  position: relative;
}

.dropdown-container {
  position: relative;
}

.tagged-input-wrapper {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-height: 2.5rem;
  padding: 0.375rem 0.75rem 0.375rem 2.25rem;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  background-color: white;
  cursor: text;
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: var(--bulma-primary);
    box-shadow: 0 0 0 0.125em
      rgba(
        var(--bulma-primary-h),
        var(--bulma-primary-s),
        var(--bulma-primary-l),
        0.25
      );
  }
}

.tagged-input {
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  margin: 0;
  background: transparent;
  flex: 1;
  min-width: 120px;
  height: auto;

  &:focus {
    border: none !important;
    box-shadow: none !important;
  }

  &.has-tags {
    margin-top: 0.25rem;
  }
}

// Icon positioning
.control.has-icons-left .icon.is-left {
  position: absolute;
  z-index: 0;
  pointer-events: none;
}

.autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #dbdbdb;
  border-top: none;
  border-radius: 0 0 4px 4px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .dropdown-item {
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    border: none;

    &.is-hovered {
      background-color: hsl(
        var(--bulma-primary-h),
        var(--bulma-primary-s),
        96%
      );
    }

    &.is-disabled {
      opacity: 0.5;
      cursor: not-allowed;
      background-color: #f5f5f5;
    }

    &:not(.is-disabled):hover {
      background-color: hsl(
        var(--bulma-primary-h),
        var(--bulma-primary-s),
        96%
      );
    }
  }
}

.suggestion-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .suggestion-icon {
    width: 1rem;
    text-align: center;
  }

  .suggestion-text {
    flex: 1;
    font-weight: 500;
  }

  .suggestion-type {
    margin-left: auto;
  }
}

.search-mode-toggle-inline {
  display: flex;
  gap: 0.75rem;

  .radio {
    display: flex;
    align-items: center;
    cursor: pointer;

    input[type="radio"] {
      margin-right: 0.25rem;
    }

    .radio-text {
      font-size: 0.75rem;
      color: var(--bulma-text);
      white-space: nowrap;
    }
  }
}

// Responsive adjustments
@media screen and (max-width: 768px) {
  .tags-container {
    margin-right: 0.25rem;
  }

  .search-mode-toggle-inline {
    gap: 0.5rem;

    .radio-text {
      font-size: 0.6875rem;
    }
  }
}
</style>
